/**
 * DashboardService — shared service layer for all dashboard data operations.
 *
 * Wraps GraphQL calls via AWS Amplify's generateClient(), consistent with
 * how other FINN components (ChatBoxComponent, ReportComponent, etc.) access
 * the AppSync API.
 */

import { generateAuditedClient } from "../../../audit/auditedClient";
import {
  getDashboardCache as getDashboardCacheQuery,
  getMarketplaces as getMarketplacesQuery,
  getRegions as getRegionsQuery,
  getASFMPreProcessedData as getASFMPreProcessedDataQuery,
  asyncAthenaQuery as asyncAthenaQueryGql,
  getAppCall as getAppCallQuery,
} from "../../../graphql/queries";
import {
  saveDashboardCache as saveDashboardCacheMutation,
  submitQuestion as submitQuestionMutation,
} from "../../../graphql/mutations";
import type {
  DashboardCacheEntry,
  SaveDashboardCacheInput,
  ASFMPreProcessedDataInput,
  Marketplace,
  Region,
} from "../../../models/API";

const client = generateAuditedClient();

/**
 * Fetch a cached dashboard entry from DynamoDB via AppSync.
 *
 * @returns The cached entry, or null on cache miss / error.
 */
export async function getDashboardCache(
  pk: string,
  sk: string
): Promise<DashboardCacheEntry | null> {
  try {
    const result = await client.graphql({
      query: getDashboardCacheQuery,
      variables: { input: { pk, sk } },
    });
    return (result as any).data?.getDashboardCache ?? null;
  } catch (err) {
    console.warn("getDashboardCache failed, treating as cache miss:", err);
    return null;
  }
}

/**
 * Save a dashboard cache entry to DynamoDB via AppSync.
 */
export async function saveDashboardCache(
  entry: SaveDashboardCacheInput
): Promise<void> {
  try {
    await client.graphql({
      query: saveDashboardCacheMutation,
      variables: { input: entry },
    });
  } catch (err) {
    console.warn("saveDashboardCache failed (non-blocking):", err);
  }
}

/**
 * Fetch pre-processed Athena data via the existing getASFMPreProcessedData query.
 *
 * This wraps the same GraphQL operation that the legacy processForFinn Lambda
 * used, so the dashboard can reuse the existing backend without changes.
 */
export async function fetchPreProcessedData(
  params: ASFMPreProcessedDataInput
): Promise<any> {
  const result = await client.graphql({
    query: getASFMPreProcessedDataQuery,
    variables: { input: params },
  });
  const response = (result as any).data?.getASFMPreProcessedData;
  if (!response || response.statusCode !== 200) {
    throw new Error(response?.message ?? "Failed to fetch pre-processed data");
  }
  return typeof response.data === "string"
    ? JSON.parse(response.data)
    : response.data;
}

/** Polling config for async Athena queries. */
const ATHENA_POLL_INTERVAL_MS = 3000;
const ATHENA_POLL_MAX_ATTEMPTS = 60; // 60 × 3s = 3 min max wait

/**
 * Fetch pre-processed data using async start + poll pattern via the
 * dedicated async_athena_query Lambda. Bypasses the AppSync 30s timeout.
 */
export async function fetchPreProcessedDataAsync(
  params: ASFMPreProcessedDataInput
): Promise<any> {
  // Step 1: Start the query
  const startResult = await client.graphql({
    query: asyncAthenaQueryGql,
    variables: { input: { ...params, mode: "start" } as any },
  });
  const startResponse = (startResult as any).data?.asyncAthenaQuery;
  if (!startResponse || startResponse.statusCode !== 200) {
    throw new Error(
      startResponse?.message ?? "Failed to start async Athena query"
    );
  }
  const startData =
    typeof startResponse.data === "string"
      ? JSON.parse(startResponse.data)
      : startResponse.data;
  const queryExecutionId = startData?.queryExecutionId;
  if (!queryExecutionId) {
    throw new Error("No queryExecutionId returned from start");
  }

  // Step 2: Poll until complete
  for (let attempt = 0; attempt < ATHENA_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) =>
      setTimeout(resolve, ATHENA_POLL_INTERVAL_MS)
    );

    const pollResult = await client.graphql({
      query: asyncAthenaQueryGql,
      variables: {
        input: {
          mode: "poll",
          query_execution_id: queryExecutionId,
          max_results: params.max_results,
        } as any,
      },
    });
    const pollResponse = (pollResult as any).data?.asyncAthenaQuery;
    if (!pollResponse || pollResponse.statusCode !== 200) {
      throw new Error(
        pollResponse?.message ?? "Failed to poll async Athena query"
      );
    }
    const pollData =
      typeof pollResponse.data === "string"
        ? JSON.parse(pollResponse.data)
        : pollResponse.data;

    if (pollData?.state === "SUCCEEDED") {
      return pollData;
    }
    if (pollData?.state && !["RUNNING", "QUEUED"].includes(pollData.state)) {
      throw new Error(`Athena query failed with state: ${pollData.state}`);
    }
  }

  throw new Error("Athena query timed out after polling");
}

/** Default polling config for AI summary generation. */
const AI_POLL_INTERVAL_MS = 3000;
const AI_POLL_MAX_ATTEMPTS = 40; // 40 × 3s = 2 min max wait

/**
 * Generate an AI summary using the async submitQuestion + poll pattern.
 *
 * Since the dashboard uses app user_id=0 (non-positive), the Lambda saves
 * results via putAppCall (not putConversation). We poll getAppCall to
 * retrieve the response.
 *
 * Flow:
 * 1. `submitQuestion` puts the prompt on SQS (returns immediately)
 * 2. Lambda sees user_id=0 → calls SPBFinn.ask() → saves via putAppCall
 *    with app_id=user_id(0), call_id=conversation_id
 * 3. Poll `getAppCall(app_id=0, call_id=conversationId)` until response
 *
 * @param conversationId - Unique call ID for this summary request
 * @param userId         - App user ID (0 for revenue bridging dashboard)
 * @param teamId         - Team ID (1 for FINN team)
 * @param question       - The prompt / question to send to the AI
 * @returns The AI response text
 */
export async function generateAISummary(
  conversationId: string,
  userId: number,
  teamId: number,
  question: string
): Promise<string> {
  // Step 1: Submit the question (async — goes to SQS → Lambda → DynamoDB)
  const submitResult = await client.graphql({
    query: submitQuestionMutation,
    variables: {
      input: {
        conversation_id: conversationId,
        user_id: userId,
        team_id: teamId,
        question,
      },
    },
  });
  const submitResponse = (submitResult as any).data?.submitQuestion;
  if (!submitResponse?.success) {
    throw new Error(
      submitResponse?.message ?? "Failed to submit AI summary question"
    );
  }
  const questionId = submitResponse.question_id;

  // Step 2: Poll getAppCall until the response is available
  // Lambda saves via putAppCall with app_id=userId, call_id=conversationId
  for (let attempt = 0; attempt < AI_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, AI_POLL_INTERVAL_MS));

    try {
      const pollResult = await client.graphql({
        query: getAppCallQuery,
        variables: {
          input: {
            app_id: userId,
            call_id: conversationId,
          },
        },
      });
      const calls = (pollResult as any).data?.getAppCall ?? [];
      const answered = calls.find(
        (c: any) => c.question_id === questionId && c.response
      );
      if (answered) {
        return answered.response;
      }
    } catch (err) {
      console.warn(`AI summary poll attempt ${attempt + 1} failed:`, err);
      // Continue polling despite transient errors
    }
  }

  throw new Error("AI summary generation timed out after polling");
}

/**
 * Fetch all active marketplaces from the RDS database via GraphQL.
 */
export async function fetchMarketplaces(): Promise<Marketplace[]> {
  const result = await client.graphql({
    query: getMarketplacesQuery,
  });
  return ((result as any).data?.getMarketplaces as Marketplace[]) ?? [];
}

/**
 * Fetch all active regions (with nested marketplace data) from the RDS database via GraphQL.
 */
export async function fetchRegions(): Promise<Region[]> {
  const result = await client.graphql({
    query: getRegionsQuery,
  });
  return ((result as any).data?.getRegions as Region[]) ?? [];
}
